import type { FC } from 'react';
import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import CreateFolderIcon from '@wix/da-ds/pkg/Icons/24x24/CreateFolder';
import LargeCreateFolderIcon from '@wix/da-ds/pkg/Icons/32x32/CreateFolder';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import { AddNewFolderClickBiEvent, BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import { GallectionResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { getIsRootFolder, getSelectedFolder } from '../../selectors/studio';

interface Props {
  label: string;
  folderType: 'stash' | 'gallery';
}

const CreateFolderButton: FC<Props> = ({ label, folderType }) => {
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const selectedFolder = useSelector(getSelectedFolder);
  const isRootFolder = useSelector(getIsRootFolder);

  const onCreateFolder = useCallback(() => {
    dispatch(pushModal(ModalType.STUDIO_FOLDER, { params: { folderType } }));
  }, [dispatch, folderType]);

  const biData = BiData<AddNewFolderClickBiEvent>({
    evid: 40,
    typeid: folderType === 'stash' ? GallectionResourceType.STASH : GallectionResourceType.GALLERY,
    itemid: isRootFolder ? undefined : selectedFolder?.folderId,
    sectionname: BiContextProvidedValue,
  });

  if (isMobile) {
    return (
      <IconButton
        icon={LargeCreateFolderIcon}
        iconSizeOverride={IconSize.LARGE}
        size="large"
        variant="main"
        onClick={onCreateFolder}
        aria-label={label}
        noPaddings
        biData={biData}
      />
    );
  }

  return (
    <TextButton
      icon={CreateFolderIcon}
      size="medium"
      variant="main"
      onClick={onCreateFolder}
      biData={biData}
    >
      {label}
    </TextButton>
  );
};
CreateFolderButton.displayName = 'CreateFolderButton';

export default CreateFolderButton;
